import type { ReactNode } from 'react';
import {
  ArrowRight,
  BarChart3,
  BookOpenCheck,
  FileText,
  Gift,
  Globe2,
  Inbox,
  KeyRound,
  Languages,
  Library,
  MessageSquareText,
  Mic,
  Radar,
  TrendingUp,
} from 'lucide-react';
import { Badge, Card, IconTile } from '@/components/ui';

/**
 * Säljpresentation inför uppföljningsmötet — synlig endast för mottagarna i
 * VITE_PRESENTATION_EMAILS (se useSalesPreview).
 *
 * Skriven för kundens ögon: Linnea + Fredrik Benson (VD). Inga priser, inga
 * interna tidsuppskattningar, inga marginaler — allt sådant tas muntligt.
 *
 * Siffrorna nedan är hämtade ur produktionsdatabasen 2026-08-05 och är
 * MANUELLT INKLISTRADE, inte live. Uppdatera SIFFROR + DATA_PER innan mötet
 * om det gått en tid.
 */

const DATA_PER = '5 augusti 2026';
const I_DRIFT_SEDAN = '23 mars 2026';

/** Sätt till t.ex. 'den 19 augusti' när mötet är bokat — annars generisk text. */
const MOTESDATUM: string | null = null;

interface Stat {
  icon: ReactNode;
  value: string;
  label: string;
  hint: string;
}

const SIFFROR: Stat[] = [
  {
    icon: <MessageSquareText size={16} strokeWidth={2} />,
    value: '544',
    label: 'frågor besvarade',
    hint: 'ställda till ELvis sedan starten',
  },
  {
    icon: <FileText size={16} strokeWidth={2} />,
    value: '153',
    label: 'mailutkast',
    hint: 'skrivna av Elvira åt kansliet',
  },
  {
    icon: <Library size={16} strokeWidth={2} />,
    value: '253',
    label: 'källor i kunskapsbasen',
    hint: 'lagtext, egna dokument och webbsidor',
  },
  {
    icon: <BarChart3 size={16} strokeWidth={2} />,
    value: '4,5',
    label: 'månader i drift',
    hint: `i dagligt bruk sedan ${I_DRIFT_SEDAN}`,
  },
];

interface Signal {
  icon: ReactNode;
  title: string;
  body: string;
}

const SIGNALER: Signal[] = [
  {
    icon: <TrendingUp size={16} strokeWidth={2} />,
    title: 'Ni lägger allt svårare uppgifter på Elvira',
    body: 'De mail hon skriver har nästan dubblerats i längd sedan starten — från korta standardsvar till den tyngre korrespondensen. Det är motsatsen till vad som brukar hända med nya verktyg, som oftast används mindre och ytligare med tiden.',
  },
  {
    icon: <Languages size={16} strokeWidth={2} />,
    title: 'Mer än hälften av augustis mail skrevs på engelska',
    body: 'Den internationella producentkorrespondensen är den som tar längst tid att formulera själv, och det är precis den ni börjat lämna över.',
  },
];

interface Forslag {
  icon: ReactNode;
  title: string;
  what: string;
  win: string;
}

const FORSLAG: Forslag[] = [
  {
    icon: <FileText size={18} strokeWidth={1.75} />,
    title: 'Skrivelse-assistenten',
    what: 'Elvira hjälper er med inkommande mail. Skrivelse-assistenten gör samma sak åt andra hållet: producentbrev, deadline-påminnelser och begripliga sammanfattningar av regeländringar — färdiga att skicka.',
    win: 'Utskick som idag tar en halvdag att formulera blir en genomläsning. Alltid grundat i kunskapsbasen, alltid med källhänvisning.',
  },
  {
    icon: <Mic size={18} strokeWidth={1.75} />,
    title: 'Mötesloggen med transkribering',
    what: 'Spela in eller ladda upp mötet — ni får tillbaka en sammanfattning, nyckelpunkter och en lista på vad som ska göras och av vem.',
    win: 'Ingen behöver anteckna under mötet. Och fattas ett beslut som saknar stöd i kunskapsbasen flaggas det automatiskt, så att luckan täpps till innan den blir ett problem.',
  },
  {
    icon: <Radar size={18} strokeWidth={1.75} />,
    title: 'Regelradar',
    what: 'Bevakar löpande att de lagar och EU-direktiv ni stödjer er på inte ändrats. Sker en ändring får ni en notis med en begriplig förklaring av vad den innebär för er.',
    win: 'Ni slutar behöva lita på att någon råkar läsa rätt nyhetsbrev i rätt vecka. Regeländringar når er automatiskt, översatta till vad de betyder i praktiken.',
  },
  {
    icon: <Inbox size={18} strokeWidth={1.75} />,
    title: 'Elvira i Outlook',
    what: 'Idag klistrar ni in producentmailen i ELvis Hub. Med den här kopplingen ligger svarsutkastet redan i Outlook när ni öppnar mailet.',
    win: 'Noll klipp och klistra. Verktyget flyttar in där arbetet faktiskt görs, i stället för tvärtom.',
  },
  {
    icon: <Globe2 size={18} strokeWidth={1.75} />,
    title: 'ELvis för producenterna',
    what: 'Samma kunskapsbas, men tillgänglig direkt på el-kretsen.se. Producenterna får svar dygnet runt, med hänvisning till källan.',
    win: 'Frågorna ni besvarar om och om igen besvarar sig själva. Och de frågor ELvis inte klarar samlas upp åt er — så ni ser svart på vitt vad marknaden faktiskt undrar över.',
  },
  {
    icon: <KeyRound size={18} strokeWidth={1.75} />,
    title: 'Inloggning med ert Microsoft-konto',
    what: 'Alla på kontoret loggar in med samma konto som de redan använder till Outlook. Inga nya lösenord, inga konton att lägga upp — den som finns i er katalog kommer in, den som slutar kommer inte in.',
    win: 'Idag delar ni ett gemensamt konto, vilket gör det omöjligt att se vem verktyget faktiskt hjälper. Med egen inloggning per person kan vi visa vilka avdelningar som får ut mest — och var stödet behöver bli bättre.',
  },
];

export function NastaStegPage() {
  return (
    <div className="flex-1 overflow-y-auto p-5 lg:p-6">
      <div className="max-w-[1000px] mx-auto pb-10 space-y-4">
        <header className="flex items-center justify-between gap-4 flex-wrap">
          <p className="text-[12.5px] text-ink-500">
            Underlag inför uppföljningen — Linnea &amp; Fredrik
          </p>
          <span className="text-[11px] text-ink-500 shrink-0">Siffror per {DATA_PER}</span>
        </header>

        {/* Intro */}
        <Card className="p-5 bg-brand-50 border-brand-100">
          <h2 className="text-display text-[17px] text-ink-900 leading-tight mb-2">
            Från pilot till dagligt verktyg
          </h2>
          <p className="text-[13.5px] text-ink-600 leading-relaxed max-w-2xl">
            ELvis Hub har varit i skarp drift sedan i våras. Innan vi pratar om vad som kommer
            härnäst tycker vi att ni ska få se vad som faktiskt hänt under tiden — och en sak som
            inte fungerat som tänkt.
          </p>
        </Card>

        {/* Siffror */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {SIFFROR.map((s) => (
            <Card key={s.label} variant="glass" className="p-4">
              <IconTile icon={s.icon} tone="brand" size="sm" className="mb-3" />
              <div className="text-display text-[22px] text-ink-900 leading-none">{s.value}</div>
              <div className="text-[12.5px] font-bold text-ink-700 mt-1.5 leading-tight">
                {s.label}
              </div>
              <p className="text-[11.5px] text-ink-500 leading-snug mt-1">{s.hint}</p>
            </Card>
          ))}
        </div>

        {/* Signaler i datan */}
        <div className="grid md:grid-cols-2 gap-3">
          {SIGNALER.map((sig) => (
            <Card key={sig.title} variant="glass" className="p-5">
              <div className="flex items-start gap-3">
                <IconTile icon={sig.icon} tone="success" size="md" />
                <div>
                  <h3 className="text-[14px] font-bold text-ink-900 leading-tight mb-1.5">
                    {sig.title}
                  </h3>
                  <p className="text-[12.5px] text-ink-600 leading-relaxed">{sig.body}</p>
                </div>
              </div>
            </Card>
          ))}
        </div>

        {/* Ärlighetspunkten */}
        <Card variant="glass" className="p-5 border-amber-100">
          <div className="flex items-start gap-3">
            <IconTile icon={<BookOpenCheck size={16} strokeWidth={2} />} tone="warning" size="md" />
            <div className="flex-1">
              <div className="flex items-center gap-3 flex-wrap mb-1.5">
                <h3 className="text-[14px] font-bold text-ink-900 leading-tight">
                  67 frågor har ELvis inte kunnat svara på
                </h3>
                <Badge variant="warning">Att åtgärda</Badge>
              </div>
              <p className="text-[12.5px] text-ink-600 leading-relaxed">
                De ligger sparade i Granskning, redo att besvaras — men ingen har blivit påmind om
                att gå igenom dem, så ingen har gjorts. Varje sådan fråga är någon som inte fick
                hjälp, och samtidigt den tydligaste vägvisningen vi har till vad kunskapsbasen
                behöver kompletteras med. Det är den här typen av sak vi vill fånga upp
                systematiskt framöver — se Ledningsrapporten längre ner.
              </p>
            </div>
          </div>
        </Card>

        {/* Förslag */}
        <div className="pt-2">
          <h2 className="text-display text-[18px] text-ink-900 leading-none mb-1">
            Sex förslag på vad vi bygger härnäst
          </h2>
          <p className="text-[12.5px] text-ink-500 mb-4">
            Alla sex vilar på det som redan står — samma kunskapsbas, samma källhänvisningar, samma
            krav på att aldrig gissa.
          </p>

          <div className="space-y-3">
            {FORSLAG.map((f, i) => (
              <Card key={f.title} variant="glass" className="p-5">
                <div className="flex items-start gap-4">
                  <IconTile icon={f.icon} tone="neutral" size="md" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2.5 mb-1.5">
                      <span className="text-[11px] font-semibold text-ink-500 tabular-nums">
                        {String(i + 1).padStart(2, '0')}
                      </span>
                      <h3 className="text-[15px] font-bold text-ink-900 leading-tight">
                        {f.title}
                      </h3>
                    </div>
                    <p className="text-[13px] text-ink-600 leading-relaxed">{f.what}</p>
                    <div className="flex items-start gap-2 mt-2.5 pt-2.5 border-t border-ink-100">
                      <ArrowRight
                        size={14}
                        strokeWidth={2.25}
                        className="text-emerald-600 mt-0.5 shrink-0"
                      />
                      <p className="text-[12.5px] text-ink-700 leading-relaxed font-medium">
                        {f.win}
                      </p>
                    </div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>

        {/* Bonus */}
        <Card className="p-5 bg-emerald-50 border-emerald-200">
          <div className="flex items-start gap-4">
            <IconTile icon={<Gift size={18} strokeWidth={1.75} />} tone="success" size="lg" />
            <div className="flex-1">
              <div className="flex items-center gap-3 flex-wrap mb-1.5">
                <h2 className="text-display text-[17px] text-ink-900 leading-tight">
                  Ledningsrapporten
                </h2>
                <Badge variant="success">Ingår utan kostnad vid hela paketet</Badge>
              </div>
              <p className="text-[13px] text-ink-600 leading-relaxed max-w-2xl">
                Den första varje månad landar en sammanställning i inkorgen: vad kansliet frågat om,
                vad som inte kunnat besvaras, hur kunskapsbasen vuxit, vad Elvira hjälpt till med
                och vilka regeländringar som fångats upp. Färdig att läsa — eller att skicka vidare
                till styrelsen utan att någon behöver sammanställa något.
              </p>
              <p className="text-[12.5px] text-ink-500 leading-relaxed max-w-2xl mt-2">
                Det är också den som ser till att sådant som de 67 obesvarade frågorna inte blir
                liggande igen.
              </p>
            </div>
          </div>
        </Card>

        {/* Avslut */}
        <Card variant="glass" className="p-5">
          <div className="flex items-center gap-3">
            <IconTile icon={<ArrowRight size={15} strokeWidth={2} />} tone="brand" size="sm" />
            <p className="text-[12.5px] text-ink-600 leading-relaxed">
              Vi går igenom det här tillsammans{' '}
              {MOTESDATUM ? <strong className="text-ink-800">{MOTESDATUM}</strong> : 'på uppföljningen'}.
              Läs igenom i lugn och ro innan dess — dyker det upp frågor eller något ni saknar i
              listan, hör gärna av er i förväg så tar vi med det.
            </p>
          </div>
        </Card>
      </div>
    </div>
  );
}
